import { FC } from "react";

import { type CourseAssessmentSummaryData } from "api/api-data-types";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, THead, TR, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { Link } from "utils/routing";
import { courseHomeUrl } from "utils/urls";

type AssessmentSummaryTableProps = {
  data: CourseAssessmentSummaryData[];
};

export const AssessmentSummaryTable: FC<AssessmentSummaryTableProps> = ({ data }) => {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  const formatPercentage = (value: number) => {
    return `${value}%`;
  };

  return (
    <ScrollableTableContainer className="mb-0 max-h-[400px] overflow-y-auto">
      <Table>
        <THead>
          <tr>
            <TH>
              <Tooltip place="top" text={t("assessment_summary_report.columns.code_tooltip")}>
                {t("assessment_summary_report.columns.code")}
              </Tooltip>
            </TH>

            <TH>
              <Tooltip
                place="top"
                text={t("assessment_summary_report.columns.course_title_tooltip")}
              >
                {t("assessment_summary_report.columns.course_title")}
              </Tooltip>
            </TH>

            <TH>
              <Tooltip
                place="top"
                text={t("assessment_summary_report.columns.instructors_tooltip")}
              >
                {t("assessment_summary_report.columns.instructors")}
              </Tooltip>
            </TH>

            <TH>
              <Tooltip
                place="top"
                text={t("assessment_summary_report.columns.last_assessed_tooltip")}
              >
                {t("assessment_summary_report.columns.last_assessed")}
              </Tooltip>
            </TH>

            <TH>
              <Tooltip
                place="top"
                text={t("assessment_summary_report.columns.percent_assessed_tooltip")}
              >
                {t("assessment_summary_report.columns.percent_assessed")}
              </Tooltip>
            </TH>

            <TH>
              <Tooltip
                place="top"
                text={t("assessment_summary_report.columns.percent_with_comments_tooltip")}
              >
                {t("assessment_summary_report.columns.percent_with_comments")}
              </Tooltip>
            </TH>

            <TH>
              <Tooltip
                place="top"
                text={t("assessment_summary_report.columns.comment_length_tooltip")}
              >
                {t("assessment_summary_report.columns.comment_length")}
              </Tooltip>
            </TH>

            <TH>
              <Tooltip place="top" text={t("assessment_summary_report.columns.wait_time_tooltip")}>
                {t("assessment_summary_report.columns.wait_time")}
              </Tooltip>
            </TH>

            <TH>
              <Tooltip
                place="top"
                text={t("assessment_summary_report.columns.assessments_per_student_tooltip")}
              >
                {t("assessment_summary_report.columns.assessments_per_student")}
              </Tooltip>
            </TH>
          </tr>
        </THead>

        <tbody>
          {data.map((row) => (
            <TR key={row.id}>
              <TD>
                <Tooltip place="left-start" text={row.course_code_title}>
                  {row.course_code_title}
                </Tooltip>
              </TD>

              <TD>
                <Tooltip place="left-start" text={row.title}>
                  <Link
                    className="block text-blue hover:text-blue-shade-40"
                    title={row.title}
                    to={`${courseHomeUrl(row.id)}?tab=assessments`}
                  >
                    {row.title}
                  </Link>
                </Tooltip>
              </TD>

              <TD>
                <Tooltip place="left-start" text={row.instructor_names.join(", ")}>
                  {row.instructor_names.join(", ")}
                </Tooltip>
              </TD>

              <TD className="text-center">{formatDate(row.date_last_assessed)}</TD>
              <TD className="text-right">{formatPercentage(row.assessment_percentage)}</TD>
              <TD className="text-right">
                {row.assessment_percentage > 0 ? formatPercentage(row.comment_percentage) : "-"}
              </TD>
              <TD className="text-right">
                {row.assessment_percentage > 0 ? row.avg_comment_length : "-"}
              </TD>
              <TD className="text-right">
                {row.assessment_percentage > 0 ? row.avg_assessment_delay_days : "-"}
              </TD>
              <TD className="text-right">
                {row.assessment_percentage > 0 ? row.avg_student_assessments : "-"}
              </TD>
            </TR>
          ))}
        </tbody>
      </Table>
    </ScrollableTableContainer>
  );
};
